import React, { createContext, FC, useContext, useState } from 'react';
import { UserInfo } from '../../domain/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useSWRConfig } from 'swr';

export interface AuthModel {
  error?: string;
  isAuthenticated?: boolean;
  getAnonymousUserId: () => string;
  user?: UserInfo;
  onLogout: () => void;
}

export interface CoreContextModel {
  auth: AuthModel;
}

interface PrivateCoreContextModel {
  auth: AuthModel & {
    setCustomLogout: (newCustomLogout: CustomLogoutCallback | undefined) => void;
  };
}

type CustomLogoutCallback = (defaultLogoutCallback: () => void) => void;

const CoreContext = createContext<PrivateCoreContextModel | undefined>(undefined);

interface Props {
  coreContext: CoreContextModel;
}

export const CoreContextProvider: FC<React.PropsWithChildren<Props>> = ({ children, coreContext }) => {
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();

  const customLogoutInitialValue: CustomLogoutCallback = (defaultLogoutCallback) => {
    defaultLogoutCallback();
  };

  // When passing a function as the initial state, React will call this function and use the return value as the initial state.
  // https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
  // To set the initial value to a function, we need to return the function in a new function.
  const [customLogout, setCustomLogout] = useState<CustomLogoutCallback>(() => customLogoutInitialValue);

  const _onLogout = () => {
    mutate(() => true, undefined, { revalidate: false }).then(() => {
      queryClient.cancelQueries();

      customLogout(coreContext.auth.onLogout);
    });
  };

  const _setCustomLogout = (newCustomLogout: CustomLogoutCallback | undefined) => {
    // _setCustomLogout is necessary, because if a state is set to function, React will invoke that function and use its return value as the state.
    // As we want to set the customLogout to a function, we need to return newCustomLogout in a new function.
    // See link above.
    setCustomLogout(() => newCustomLogout ?? customLogoutInitialValue);
  };

  return (
    <CoreContext.Provider
      value={{
        ...coreContext,
        auth: {
          ...coreContext.auth,
          onLogout: _onLogout,
          setCustomLogout: _setCustomLogout,
        },
      }}
    >
      {children}
    </CoreContext.Provider>
  );
};

export const useCore = () => {
  const context = useContext(CoreContext);

  if (!context) {
    throw new Error(`useCoreContext must be used within a CoreContextProvider`);
  }

  return context;
};
